import React, { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Plus, X, History, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import { money, priceForPO, itemTaxRate } from './poLine';
import { AddressAutocomplete } from '../../../components/AddressAutocomplete';
import { CURRENCIES, getOrgLocale } from '../../../lib/locale';

const today = () => new Date().toISOString().split('T')[0];
const blank = () => ({ supplier_id: '', expected_delivery_date: today(), supplier_reference: '', delivery_address: '', delivery_instructions: '', notes: '', internal_notes: '', line_items: [], currency: getOrgLocale().currency, exchange_rate: 1, prices_include_tax: getOrgLocale().po_prices_include_tax });

const supplierTerms = (item, supplierId) => (item.suppliers || []).find((s) => s.supplier_id === supplierId) || {};

const lineFromItem = (item, supplierId, qty, poInc) => {
  const s = supplierTerms(item, supplierId);
  return {
    item_id: item.id, sku: item.sku || '', description: item.name, unit: s.order_unit || item.unit || '',
    supplier_item_code: s.supplier_item_code || '', quantity_ordered: qty || s.min_order_qty || item.reorder_qty || 1,
    price_per_unit: priceForPO(s.price ?? s.last_price ?? item.cost_price ?? 0, item, poInc), tax_rate: itemTaxRate(item),
  };
};

export default function GeneralPODialog({ open, onOpenChange, suppliers, onSubmit, po, prefill }) {
  const [form, setForm] = useState(blank());
  const [items, setItems] = useState([]);
  const [backorders, setBackorders] = useState([]);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!open) { return; }
    api.get('/general-inventory/items').then((r) => setItems(r.data || [])).catch(() => setItems([]));
    if (po) {
      const inc = !!po.prices_include_tax;
      const lines = po.line_items || [];
      // An inclusive PO where no line carries GST was saved before GST ticks existed: apply the company rate.
      const fixGst = inc && lines.length > 0 && lines.every((l) => l.is_backorder || !(Number(l.tax_rate) > 0));
      const line_items = fixGst ? lines.map((l) => (l.is_backorder ? l : { ...l, tax_rate: getOrgLocale().tax_rate })) : lines;
      setForm({ ...blank(), ...po, currency: po.currency || getOrgLocale().currency, exchange_rate: po.exchange_rate || 1, expected_delivery_date: (po.expected_delivery_date || '').slice(0, 10), line_items, prices_include_tax: inc });
    } else {
      setForm({ ...blank(), ...(prefill || {}) });
    }
    setSearch('');
  }, [open, po, prefill]);

  useEffect(() => {
    if (!open || !form.supplier_id) { setBackorders([]); return; }
    api.get('/purchase-orders/backorders', { params: { supplier_id: form.supplier_id } })
      .then((r) => setBackorders(r.data || [])).catch(() => setBackorders([]));
  }, [open, form.supplier_id]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setLine = (i, k, v) => setForm((f) => ({ ...f, line_items: f.line_items.map((l, j) => (j === i ? { ...l, [k]: v } : l)) }));
  const addLines = (lines) => setForm((f) => ({ ...f, line_items: [...f.line_items, ...lines] }));
  const removeLine = (i) => setForm((f) => ({ ...f, line_items: f.line_items.filter((_, j) => j !== i) }));

  const supplierItems = items.filter((it) => (it.suppliers || []).some((s) => s.supplier_id === form.supplier_id));
  const matches = (search ? items.filter((it) => `${it.name} ${it.sku} ${it.barcode} ${it.category}`.toLowerCase().includes(search.toLowerCase())) : supplierItems).slice(0, 12);
  const onPO = new Set(form.line_items.map((l) => l.backorder_line_id).filter(Boolean));

  const addLowStock = async () => {
    const r = await api.get('/general-inventory/reorder');
    const mine = (r.data || []).filter((it) => (it.suppliers || []).some((s) => s.supplier_id === form.supplier_id));
    const have = new Set(form.line_items.filter((l) => !l.is_backorder).map((l) => l.item_id));
    const fresh = mine.filter((it) => !have.has(it.id));
    addLines(fresh.map((it) => lineFromItem(it, form.supplier_id, it.suggested_qty, form.prices_include_tax)));
    toast[fresh.length ? 'success' : 'info'](fresh.length ? `${fresh.length} low-stock item(s) added` : 'No low-stock items for this supplier');
  };

  const addBackorder = (b) => addLines([{
    item_id: b.item_id, material_id: b.material_id, sku: b.sku, description: b.description, unit: b.unit,
    supplier_item_code: b.supplier_item_code, quantity_ordered: b.outstanding, price_per_unit: b.price_per_unit, tax_rate: 0,
    is_backorder: true, backorder_po_id: b.po_id, backorder_po_number: b.po_number, backorder_line_id: b.line_id,
  }]);

  const lineTotal = (l) => (l.is_backorder ? 0 : (Number(l.quantity_ordered) || 0) * (Number(l.price_per_unit) || 0));
  const gross = form.line_items.reduce((s, l) => s + lineTotal(l), 0);
  const inc = !!form.prices_include_tax;
  const tax = form.line_items.reduce((s, l) => { const r = Number(l.tax_rate) || 0; return s + lineTotal(l) * (inc ? r / (100 + r) : r / 100); }, 0);
  const subtotal = inc ? gross - tax : gross;

  const submit = () => {
    if (!form.supplier_id) { toast.error('Choose a supplier'); return; }
    if (!form.line_items.length) { toast.error('Add at least one line'); return; }
    if (form.line_items.some((l) => !l.description && !l.item_id)) { toast.error('Each line needs a description'); return; }
    onSubmit({ ...form, po_type: 'General', expected_delivery_date: form.expected_delivery_date || today(), exchange_rate: foreign ? Number(form.exchange_rate) || 1 : 1 });
  };
  const home = getOrgLocale();
  const foreign = form.currency && form.currency !== home.currency;
  const cur = form.currency;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto" data-testid="general-po-dialog">
        <DialogHeader>
          <DialogTitle>{po ? `Edit ${po.po_number}` : 'New General Purchase Order'}</DialogTitle>
          <DialogDescription>Pull items from General Inventory, add back orders as reminders, or type a line.</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div className="md:col-span-2">
            <Label>Supplier *</Label>
            <Select value={form.supplier_id} onValueChange={(v) => set('supplier_id', v)}>
              <SelectTrigger data-testid="gpo-supplier"><SelectValue placeholder="Select supplier" /></SelectTrigger>
              <SelectContent>
                {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.company_name} ({s.supplier_code})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Delivery date</Label>
            <Input type="date" value={form.expected_delivery_date} onChange={(e) => set('expected_delivery_date', e.target.value)} data-testid="gpo-date" />
          </div>
          <div>
            <Label>Supplier reference / quote no.</Label>
            <Input value={form.supplier_reference || ''} onChange={(e) => set('supplier_reference', e.target.value)} data-testid="gpo-ref" />
          </div>
          <div className="md:col-span-2">
            <Label>Deliver to</Label>
            <AddressAutocomplete inputComponent={Input} value={form.delivery_address || ''} onChange={(v) => set('delivery_address', v)} onSelect={(a) => set('delivery_address', a.full)} placeholder="Start typing the delivery address" testid="gpo-address" />
          </div>
          <div>
            <Label>Currency</Label>
            <select className="border rounded-md h-10 px-2 w-full text-sm" value={form.currency} onChange={(e) => set('currency', e.target.value)} data-testid="gpo-currency">
              {CURRENCIES.map((c) => <option key={c.code} value={c.code}>{c.code} — {c.name}</option>)}
            </select>
          </div>
          <div>
            <Label>{foreign ? `1 ${form.currency} = ? ${home.currency}` : 'Exchange rate'}</Label>
            <Input type="number" step="any" min="0" disabled={!foreign} value={foreign ? form.exchange_rate : 1} onChange={(e) => set('exchange_rate', e.target.value)} data-testid="gpo-rate" />
          </div>
        </div>

        {form.supplier_id && backorders.length > 0 && (
          <div className="border border-amber-300 bg-amber-50 rounded-lg p-3" data-testid="gpo-backorders">
            <div className="flex items-center gap-2 font-semibold text-amber-900 text-sm mb-2">
              <History className="h-4 w-4" /> This supplier still owes you {backorders.length} back-order line{backorders.length > 1 ? 's' : ''}
            </div>
            <div className="space-y-1">
              {backorders.map((b) => (
                <div key={b.line_id} className="flex flex-wrap items-center gap-2 text-sm bg-white rounded border px-2 py-1" data-testid={`gpo-bo-${b.line_id}`}>
                  <Badge variant="outline" className="border-amber-400 text-amber-800">{b.po_number}</Badge>
                  <span className="font-medium">{b.description}</span>
                  <span className="text-slate-500">{b.outstanding} {b.unit} outstanding</span>
                  {b.reminded_on.length > 0 && <span className="text-xs text-slate-400">already on {b.reminded_on.join(', ')}</span>}
                  <Button size="sm" variant="outline" className="ml-auto h-7" disabled={onPO.has(b.line_id)} onClick={() => addBackorder(b)} data-testid={`gpo-bo-add-${b.line_id}`}>
                    {onPO.has(b.line_id) ? 'Added' : 'Add as reminder'}
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="bg-slate-50 rounded-lg p-3 space-y-2">
          <div className="flex flex-wrap gap-2 items-center">
            <Input className="flex-1 min-w-[200px]" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={form.supplier_id ? "Search inventory (this supplier's items shown first)" : 'Search inventory'} data-testid="gpo-item-search" />
            <Button variant="outline" onClick={addLowStock} disabled={!form.supplier_id} data-testid="gpo-add-lowstock"><AlertTriangle className="h-4 w-4 mr-1" /> Add low-stock items</Button>
            <Button variant="outline" onClick={() => addLines([{ description: '', quantity_ordered: 1, price_per_unit: 0, unit: '', tax_rate: 0 }])} data-testid="gpo-add-manual"><Plus className="h-4 w-4 mr-1" /> Manual line</Button>
          </div>
          {matches.length > 0 && (
            <div className="flex flex-wrap gap-1" data-testid="gpo-item-matches">
              {matches.map((it) => {
                const s = supplierTerms(it, form.supplier_id);
                return (
                  <button key={it.id} type="button" onClick={() => addLines([lineFromItem(it, form.supplier_id, undefined, form.prices_include_tax)])} className="text-xs border rounded-full px-3 py-1 bg-white hover:bg-slate-100 transition-colors" data-testid={`gpo-pick-${it.id}`}>
                    + {it.name}{it.sku ? ` (${it.sku})` : ''}{s.price != null ? ` · ${money(s.price)}` : ''} · {it.quantity_on_hand ?? 0} on hand
                  </button>
                );
              })}
            </div>
          )}
          {!search && form.supplier_id && supplierItems.length === 0 && <p className="text-xs text-slate-500">No inventory items list this supplier yet — search all items or add a manual line.</p>}
        </div>

        <div className="flex flex-wrap items-center gap-3 text-sm">
          <label className="flex items-center gap-2 font-medium border rounded-md px-3 py-1.5 bg-white"><Checkbox checked={inc} onCheckedChange={(v) => setForm({ ...form, prices_include_tax: !!v, line_items: v ? form.line_items.map((l) => (l.is_backorder || Number(l.tax_rate) > 0 ? l : { ...l, tax_rate: home.tax_rate })) : form.line_items })} data-testid="gpo-prices-inc-tax" /> Prices include {home.tax_name}</label>
          <Button type="button" size="sm" variant="outline" onClick={() => setForm({ ...form, line_items: form.line_items.map((l) => (l.is_backorder ? l : { ...l, tax_rate: home.tax_rate })) })} disabled={!form.line_items.length} data-testid="gpo-gst-all">Tick {home.tax_name} on all lines</Button>
        </div>

        <div className="overflow-x-auto border rounded-lg">
          <table className="w-full text-sm" data-testid="gpo-lines">
            <thead className="bg-slate-100 text-slate-700">
              <tr>
                <th className="text-left px-2 py-2">Description</th>
                <th className="text-left px-2 py-2 w-28">Supplier code</th>
                <th className="text-right px-2 py-2 w-24">Qty</th>
                <th className="text-left px-2 py-2 w-20">Unit</th>
                <th className="text-right px-2 py-2 w-28">Price ({inc ? 'inc' : 'ex'} {home.tax_name})</th>
                <th className="text-center px-2 py-2 w-16">{home.tax_name}</th>
                <th className="text-right px-2 py-2 w-28">Total</th>
                <th className="w-8"></th>
              </tr>
            </thead>
            <tbody>
              {form.line_items.length === 0 && <tr><td colSpan={8} className="text-center text-slate-500 py-6">No lines yet</td></tr>}
              {form.line_items.map((l, i) => (
                <tr key={i} className={`border-t ${l.is_backorder ? 'bg-amber-50' : ''}`} data-testid={`gpo-line-${i}`}>
                  <td className="px-2 py-1">
                    <Input className="h-8" value={l.description || ''} onChange={(e) => setLine(i, 'description', e.target.value)} placeholder="Item description" disabled={l.is_backorder} data-testid={`gpo-line-desc-${i}`} />
                    {l.is_backorder && <Badge className="mt-1 bg-amber-500 text-white" data-testid={`gpo-line-bo-${i}`}>BACK ORDER — from {l.backorder_po_number}</Badge>}
                    {l.sku && !l.is_backorder && <div className="text-[11px] text-slate-500 mt-0.5">SKU {l.sku}</div>}
                  </td>
                  <td className="px-2 py-1"><Input className="h-8" value={l.supplier_item_code || ''} onChange={(e) => setLine(i, 'supplier_item_code', e.target.value)} disabled={l.is_backorder} /></td>
                  <td className="px-2 py-1"><Input className="h-8 text-right" type="number" min="0" step="any" value={l.quantity_ordered} onChange={(e) => setLine(i, 'quantity_ordered', e.target.value)} data-testid={`gpo-line-qty-${i}`} /></td>
                  <td className="px-2 py-1"><Input className="h-8" value={l.unit || ''} onChange={(e) => setLine(i, 'unit', e.target.value)} disabled={l.is_backorder} /></td>
                  <td className="px-2 py-1"><Input className="h-8 text-right" type="number" min="0" step="0.01" value={l.price_per_unit} onChange={(e) => setLine(i, 'price_per_unit', e.target.value)} disabled={l.is_backorder} data-testid={`gpo-line-price-${i}`} /></td>
                  <td className="px-2 py-1 text-center"><Checkbox checked={Number(l.tax_rate) > 0} disabled={l.is_backorder} onCheckedChange={(v) => setLine(i, 'tax_rate', v ? home.tax_rate : 0)} data-testid={`gpo-line-gst-${i}`} /></td>
                  <td className="px-2 py-1 text-right font-medium">{l.is_backorder ? <span className="text-xs text-amber-700">on {l.backorder_po_number}</span> : money(lineTotal(l), cur)}</td>
                  <td className="px-1"><Button size="sm" variant="ghost" onClick={() => removeLine(i)}><X className="h-4 w-4" /></Button></td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-slate-50 text-sm">
              <tr><td colSpan={6} className="text-right px-2 py-1">Subtotal{inc ? ` (ex ${home.tax_name})` : ''}</td><td className="text-right px-2 py-1" data-testid="gpo-subtotal">{money(subtotal, cur)}</td><td /></tr>
              <tr><td colSpan={6} className="text-right px-2 py-1">{home.tax_name}{inc ? ' included' : ''}</td><td className="text-right px-2 py-1" data-testid="gpo-tax">{money(tax, cur)}</td><td /></tr>
              <tr className="font-bold"><td colSpan={6} className="text-right px-2 py-1">Total</td><td className="text-right px-2 py-1" data-testid="gpo-total">{money(subtotal + tax, cur)}</td><td /></tr>
              {foreign && <tr className="text-slate-500"><td colSpan={6} className="text-right px-2 py-1">Total in {home.currency}</td><td className="text-right px-2 py-1" data-testid="gpo-total-home">{money((subtotal + tax) * (Number(form.exchange_rate) || 0), home.currency)}</td><td /></tr>}
            </tfoot>
          </table>
        </div>
        <p className="text-xs text-slate-500">Back-order lines remind the supplier of goods still owed. They are not charged again; goods received against them are booked to the original order.</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div className="md:col-span-2"><Label>Delivery instructions</Label><Textarea rows={2} value={form.delivery_instructions || ''} onChange={(e) => set('delivery_instructions', e.target.value)} placeholder="e.g. Deliver to rear loading dock before 10am. Call 0400 000 000 on arrival. Chilled goods to cool room." data-testid="gpo-delivery-instructions" /></div>
          <div><Label>Notes to supplier</Label><Textarea rows={2} value={form.notes || ''} onChange={(e) => set('notes', e.target.value)} /></div>
          <div><Label>Internal notes</Label><Textarea rows={2} value={form.internal_notes || ''} onChange={(e) => set('internal_notes', e.target.value)} /></div>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="flex-1" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="flex-1 bg-slate-900 hover:bg-slate-800" onClick={submit} data-testid="gpo-submit">{po ? 'Save changes' : 'Create Purchase Order'}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
